/**
 * PillButton — Layer-1 full-radius pill CTA button.
 *
 * Variants:
 *   primary        — filled navy/CTA
 *   ghost          — text-only, no border
 *   ghostOutline   — ghost with border at 15% opacity
 *   destructive    — error red
 *
 * Sizes: sm | md
 *
 * @layer-1 — propagates to all features automatically
 */
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const pillCVA = cva(
  "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] shadow-sm focus-visible:ring-[var(--color-primary)]",
        ghost:
          "text-[var(--text-primary)] hover:bg-[var(--color-surface-low)] focus-visible:ring-[var(--text-primary)]",
        ghostOutline:
          "text-[var(--text-primary)] border border-white/15 hover:bg-white/5 focus-visible:ring-[var(--text-primary)]",
        destructive:
          "bg-[var(--error)] text-white hover:bg-[var(--error)]/90 focus-visible:ring-[var(--error)]",
      },
      size: {
        sm: "h-8 px-4 text-xs",
        md: "h-10 px-5 text-sm",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

interface PillButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof pillCVA> {
  children: React.ReactNode;
}

export function PillButton({
  variant,
  size,
  className,
  children,
  ...props
}: PillButtonProps) {
  return (
    <button className={cn(pillCVA({ variant, size }), className)} {...props}>
      {children}
    </button>
  );
}
